import React from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { ArrowForward, EventBusy, FileDownload, Folder, WhatsApp } from '@mui/icons-material';
import { AtRiskRow, CancellationRow } from '../../services/dashboardService';
import { titleCaseName } from '../../utils/nameCase';
import { colors } from '../../theme/designSystem';
import { whatsAppLink } from '../../utils/phone';

/**
 * Cancelamentos e evasão (RN-524) no modelo de duas colunas.
 *
 * <p>A esquerda é o que exige ação AGORA — o que já caiu da agenda dos próximos 15 dias e quem
 * parou de vir sem retorno marcado. A direita é a leitura do mês: quem mais cancela, em taxa.
 * Antes eram dois painéis de cancelamento lado a lado (15 e 30 dias) com a evasão embaixo, e a
 * repetição escondia que os três respondem a perguntas diferentes.</p>
 *
 * <p>A taxa manda na ordem, não o total: 5 de 6 é um problema maior que 6 de 43.</p>
 */

const TOPO = 5;

/** Dentro do `.dashboard-group` o painel NÃO leva sombra própria: o container já é a caixa, e
 *  o CSS do grupo zera sombra e padding de qualquer `Paper` ali dentro (por isso aqui é `Box`).
 *  Caixa com sombra dentro de caixa com sombra é o que destoava dos painéis antigos. */
const cardSx = {
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: '1px solid #e9ecef',
  padding: '1.25rem',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

/** Rótulo de seção dentro do card — apoio, nunca concorrendo com o título do bloco. */
const Secao: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#212529', mb: 1.25 }}>{children}</Typography>
);

const taxa = (r: CancellationRow): number =>
  r.agendamentos > 0 ? Math.round((r.cancelamentos / r.agendamentos) * 100) : 0;

/**
 * Ações da linha, nas MESMAS cores dos demais botões do dashboard (verde sólido do WhatsApp,
 * azul sólido do cadastro) — o fundo pastel que eu havia usado não existe no sistema.
 *
 * <p><c>podeContatar</c> falso esconde o WhatsApp: falar com o paciente é trabalho da
 * secretaria e da coordenação, não do terapeuta (decisão do Hilton, 11/09/2026).</p>
 */
const Acoes: React.FC<{
  telefone?: string | null;
  patientId: string;
  onOpen: (id: string) => void;
  podeContatar: boolean;
}> = ({ telefone, patientId, onOpen, podeContatar }) => (
  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
    {podeContatar && telefone && (
      <Tooltip title="Falar no WhatsApp">
        <IconButton
          component="a"
          // O telefone vem formatado com DDI ("+55 (11) ..."): os dígitos já são o E.164 do wa.me.
          href={whatsAppLink(telefone) ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          sx={{ bgcolor: '#25D366', color: 'white', '&:hover': { bgcolor: '#1da851' } }}
        >
          <WhatsApp fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
    <Tooltip title="Abrir cadastro do paciente">
      <IconButton
        onClick={() => onOpen(patientId)}
        size="small"
        sx={{ bgcolor: '#03a9f4', color: 'white', '&:hover': { bgcolor: '#0288d1' } }}
      >
        <Folder fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>
);

const CancellationsSection: React.FC<{
  next15d: CancellationRow[];
  last30d: CancellationRow[];
  atRisk: AtRiskRow[];
  onOpenPatient: (patientId: string) => void;
  /** ausente = sem exportação (perfil profissional: a planilha traz nome completo e telefone) */
  onExport?: () => void;
  exporting: boolean;
  /** Páginas dos relatórios completos; só vêm quando o perfil tem acesso. */
  cancellationsReportPage?: string;
  patientFlowReportPage?: string;
  onGoTo: (page: string) => void;
  /** Falso para o Profissional: contato com o paciente é da secretaria/coordenação. */
  podeContatar: boolean;
}> = ({
  next15d,
  last30d,
  atRisk,
  onOpenPatient,
  onExport,
  exporting,
  cancellationsReportPage,
  patientFlowReportPage,
  onGoTo,
  podeContatar,
}) => {
  const proximos = next15d.slice(0, TOPO);
  const mes = last30d.slice(0, TOPO);
  const risco = atRisk.slice(0, TOPO);

  // Totais do período, somando o que a lista inteira traz (não só o top 5): é a frase do rodapé.
  const totalCancelados = last30d.reduce((s, r) => s + r.cancelamentos, 0);
  const totalAgendados = last30d.reduce((s, r) => s + r.agendamentos, 0);
  const maiorTaxa = Math.max(1, ...mes.map(taxa));

  return (
    <Box>
      {/* Cabeçalho no padrão das demais áreas do dashboard */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box className="dashboard-card-icon" sx={{ backgroundColor: `${colors.error} !important` }}>
            <EventBusy />
          </Box>
          <Box>
            {/* MESMO título dos cards do topo: h6 + `dashboard-card-title` em 1rem/600. */}
            <Typography variant="h6" className="dashboard-card-title" sx={{ fontSize: '1rem' }}>
              Cancelamentos e evasão
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#6c757d' }}>
              próximos 15 dias, últimos 30 e quem parou de vir
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 5fr) minmax(0, 7fr)' }, gap: 2, alignItems: 'stretch' }}>
        {/* ============ esquerda: o que pede ação agora ============ */}
        <Box sx={cardSx}>
          <Secao>Cancelamentos · hoje a +15 dias</Secao>

          {proximos.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Typography sx={{ fontSize: '2rem', fontWeight: 600, color: colors.success, lineHeight: 1 }}>0</Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#212529' }}>
                Nenhum cancelamento na agenda do período.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mb: 2 }}>
              {proximos.map((r) => (
                <Box
                  key={r.patientId}
                  sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1, py: 0.9, borderBottom: '1px solid #f1f3f5' }}
                >
                  <Typography
                    onClick={() => onOpenPatient(r.patientId)}
                    sx={{ fontSize: '0.875rem', color: '#212529', flex: { xs: '1 1 100%', sm: 1 }, minWidth: 0, overflowWrap: 'anywhere', cursor: 'pointer' }}
                  >
                    {titleCaseName(r.name)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#6c757d', whiteSpace: 'nowrap' }}>
                    {r.cancelamentos}/{r.agendamentos}
                  </Typography>
                  <Box sx={{ ml: 'auto', display: 'flex' }}>
                    <Acoes telefone={r.telefone} patientId={r.patientId} onOpen={onOpenPatient} podeContatar={podeContatar} />
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ borderTop: '1px solid #f1f3f5', pt: 1.75, flex: 1 }}>
            <Secao>Risco de evasão · sem retorno marcado</Secao>

            {risco.length === 0 ? (
              <Typography sx={{ fontSize: '0.875rem', color: '#6c757d' }}>
                Ninguém sem retorno marcado no período.
              </Typography>
            ) : (
              risco.map((r) => (
                <Box
                  key={r.patientId}
                  sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1, py: 0.85, borderBottom: '1px solid #f8f9fa' }}
                >
                  <Typography
                    onClick={() => onOpenPatient(r.patientId)}
                    sx={{ fontSize: '0.875rem', color: '#212529', flex: { xs: '1 1 100%', sm: 1 }, minWidth: 0, overflowWrap: 'anywhere', cursor: 'pointer' }}
                  >
                    {titleCaseName(r.name)}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${r.diasSemVir} dias sem vir`}
                    sx={{ height: 22, fontSize: '0.75rem', backgroundColor: colors.warningSoft, color: colors.warningSoftText, flexShrink: 0 }}
                  />
                  <Typography sx={{ fontSize: '0.8rem', color: '#6c757d', whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}>
                    {r.ultimoAtendimento}
                  </Typography>
                  <Box sx={{ ml: 'auto', display: 'flex' }}>
                    <Acoes telefone={r.telefone} patientId={r.patientId} onOpen={onOpenPatient} podeContatar={podeContatar} />
                  </Box>
                </Box>
              ))
            )}

            {patientFlowReportPage && atRisk.length > 0 && (
              <Button
                size="small"
                endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                onClick={() => onGoTo(patientFlowReportPage)}
                sx={{ textTransform: 'none', fontSize: '0.875rem', mt: 1, px: 0 }}
              >
                Ver relatório de evasão
              </Button>
            )}
          </Box>
        </Box>

        {/* ============ direita: a leitura do mês ============ */}
        <Box sx={cardSx}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#212529' }}>
              Maiores taxas de cancelamento
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#6c757d' }}>top 5 · 30 dias</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#6c757d', ml: 'auto', display: { xs: 'none', sm: 'block' } }}>
              canc. / agend.
            </Typography>
          </Box>

          {mes.length === 0 ? (
            <Typography sx={{ fontSize: '0.875rem', color: '#6c757d', py: 3, textAlign: 'center' }}>
              Nenhum cancelamento nos últimos 30 dias.
            </Typography>
          ) : (
            mes.map((r) => {
              const pct = taxa(r);
              return (
                <Box
                  key={r.patientId}
                  sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1.25, py: 1, borderBottom: '1px solid #f1f3f5' }}
                >
                  <Typography
                    onClick={() => onOpenPatient(r.patientId)}
                    sx={{ fontSize: '0.875rem', color: '#212529', flex: { xs: '1 1 100%', sm: 1 }, minWidth: 0, overflowWrap: 'anywhere', cursor: 'pointer' }}
                  >
                    {titleCaseName(r.name)}
                  </Typography>
                  {/* Barra proporcional à MAIOR taxa da lista: dá para comparar as cinco de relance */}
                  <Box sx={{ width: 90, flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
                    <Box sx={{ height: 6, borderRadius: 3, backgroundColor: '#eef1f4', overflow: 'hidden' }}>
                      <Box sx={{ width: `${(100 * pct) / maiorTaxa}%`, height: '100%', backgroundColor: colors.error }} />
                    </Box>
                  </Box>
                  <Typography
                    sx={{ fontSize: '0.875rem', fontWeight: 600, color: pct >= 50 ? colors.error : '#6c757d', width: { xs: 'auto', sm: 42 }, textAlign: 'right', flexShrink: 0 }}
                  >
                    {pct}%
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#6c757d', width: { xs: 'auto', sm: 44 }, textAlign: 'right', flexShrink: 0 }}>
                    {r.cancelamentos}/{r.agendamentos}
                  </Typography>
                  <Box sx={{ ml: 'auto', display: 'flex' }}>
                    <Acoes telefone={r.telefone} patientId={r.patientId} onOpen={onOpenPatient} podeContatar={podeContatar} />
                  </Box>
                </Box>
              );
            })
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pt: 1.5, mt: 'auto', flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.875rem', color: '#6c757d' }}>
              {totalCancelados} {totalCancelados === 1 ? 'cancelamento' : 'cancelamentos'} em {totalAgendados}{' '}
              {totalAgendados === 1 ? 'agendamento' : 'agendamentos'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {cancellationsReportPage && (
                <Button
                  size="small"
                  endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                  onClick={() => onGoTo(cancellationsReportPage)}
                  sx={{ textTransform: 'none', fontSize: '0.875rem' }}
                >
                  Ver relatório
                </Button>
              )}
              {onExport && (
                <Button
                  size="small"
                  startIcon={exporting ? <CircularProgress size={14} /> : <FileDownload sx={{ fontSize: 16 }} />}
                  onClick={onExport}
                  disabled={exporting}
                  sx={{ textTransform: 'none', fontSize: '0.875rem' }}
                >
                  Exportar planilha
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CancellationsSection;
